<script>
  import { _ } from "svelte-i18n";
  import { onMount } from "svelte";
  import { currentPage } from "../../utilities/DataStore";
  import { BASE_URL } from "../../utilities/config.js";
  import { sanitizeAssessmentHtml } from "../../utilities/TextSanitizer.js";
  import Loader from "./report/Loader.svelte";
  import IeltsReportHeader from "./report/IeltsReportHeader.svelte";
  import IeltsReportItemHeader from "./report/IeltsReportItemHeader.svelte";
  import { buildIeltsReadingReport } from "../ieltsReading.mjs";
  import { buildIeltsListeningReport } from "../ieltsSkills.mjs";

  export let instanceId;
  export let instanceData;
  // "SCORE" shows the band and score summary only, "DETAIL" adds the per-part
  // answer feedback.
  export let variant = "SCORE";
  // READING or LISTENING — the two skills share the parts/answer-key model;
  // LISTENING converts the raw score with the listening band table and offers
  // the exercise audio in the detailed feedback.
  export let skill = "READING";

  let reportStatus = "LOADING";
  let report = {
    parts: [],
    totalQuestions: 0,
    totalCorrect: 0,
    percentage: null,
    band: null,
  };

  // Part sections are expanded by default; incorrect answers show their
  // explanation by default (collapsible), correct answers start collapsed.
  let collapsedParts = {};
  let collapsedIncorrectRows = {};
  let expandedCorrectRows = {};

  async function loadDataProgress() {
    reportStatus = "LOADING";
    return fetch(
      BASE_URL +
        `/public/assessment/instance/` +
        instanceId +
        `/progress-report`,
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then((data) => {
        report =
          skill === "LISTENING"
            ? buildIeltsListeningReport(data)
            : buildIeltsReadingReport(data);
        reportStatus = "OK";
      })
      .catch((error) => {
        console.error("catch error", error);
        reportStatus = "ERROR";
      });
  }

  onMount(() => {
    loadDataProgress();
  });

  function togglePart(partNumber) {
    collapsedParts = {
      ...collapsedParts,
      [partNumber]: !collapsedParts[partNumber],
    };
  }

  function toggleRow(question) {
    if (question.isCorrect) {
      expandedCorrectRows = {
        ...expandedCorrectRows,
        [question.number]: !expandedCorrectRows[question.number],
      };
    } else {
      collapsedIncorrectRows = {
        ...collapsedIncorrectRows,
        [question.number]: !collapsedIncorrectRows[question.number],
      };
    }
  }

  function isRowOpen(question, expandedCorrect, collapsedIncorrect) {
    return question.isCorrect
      ? !!expandedCorrect[question.number]
      : !collapsedIncorrect[question.number];
  }

  // Incorrect answers first (the mistakes to learn from), then by number,
  // same ordering as the admin report.
  function sortedQuestions(questions) {
    return [...questions].sort((a, b) => {
      if (a.isCorrect !== b.isCorrect) {
        return a.isCorrect ? 1 : -1;
      }
      return a.number - b.number;
    });
  }

  function optionLabel(option) {
    return option.key === option.text
      ? option.text
      : `${option.key}) ${option.text}`;
  }

  function correctAnswerLabel(question) {
    if (question.options && question.options.length) {
      const match = question.options.find((o) => o.key === question.correctKey);
      if (match) return optionLabel(match);
    }
    return question.correctDisplay;
  }

  function partScore(part) {
    return `${part.questions.filter((q) => q.isCorrect).length} / ${part.questions.length}`;
  }
</script>

<div class="container ielts-report paper-shadow">
  {#if reportStatus === "LOADING"}
    <Loader />
  {:else if reportStatus === "ERROR"}
    <div class="text-center p-5">{$_("ERROR_LOADING_ASSESSMENT")}</div>
  {:else}
    <IeltsReportHeader {instanceData} band={report.band} {skill} />

    {#if variant !== "DETAIL"}
      <div class="mb-4 mt-5">
        <hr data-content={$_("SCORE_SUMMARY")} class="hr-text" />
      </div>

      <div class="row justify-content-center">
        <div class="col-md-4 col-6 text-center summary-box">
          <div class="summary-value">
            {report.totalCorrect} / {report.totalQuestions}
          </div>
          <div class="summary-label">{$_("CORRECT_ANSWERS")}</div>
        </div>
        <div class="col-md-4 col-6 text-center summary-box">
          <div class="summary-value">
            {report.percentage === null ? "-" : report.percentage + "%"}
          </div>
          <div class="summary-label">{$_("OVERALL")}</div>
        </div>
      </div>
    {/if}

    {#if variant === "DETAIL"}
      <div class="mb-2 mt-5">
        <h4 class="section-title">{$_("DETAILED_FEEDBACK")}</h4>
        <hr />
      </div>

      {#each report.parts as part (part.number)}
        <div class="mb-4">
          <IeltsReportItemHeader
            number={part.number}
            title={part.title}
            value={partScore(part)}
            label={$_("SCORE")}
            collapsed={collapsedParts[part.number]}
            toggle={() => togglePart(part.number)}
          />

          <!-- The header spans the full width; the body splits in two:
               the source material (audio + transcript for listening, the
               passage for reading) on the left, the answer key on the right. -->
          <div
            class="ielts-collapsible-body"
            class:collapsed={collapsedParts[part.number]}
          >
            <div class="row mt-3">
              {#if part.audio || part.passages.length}
                <div class="col-lg-6 mb-3">
                  {#if part.audio}
                    <div class="audio-box mb-3">
                      <span class="column-title mr-3">{$_("AUDIO")}</span>
                      <audio controls preload="none" src={part.audio}>
                        {$_("NOT_SUPPORTED")}
                      </audio>
                    </div>
                  {/if}

                  {#if part.passages.length}
                    <h6 class="column-title">
                      {$_(
                        skill === "LISTENING" ? "TRANSCRIPT" : "READING_PASSAGE",
                      )}
                    </h6>
                    {#each part.passages as passage (passage.index)}
                      <div class="passage-box ielts-avoid-break">
                        {#if passage.title}
                          <div class="passage-title">{passage.title}</div>
                        {/if}
                        <div class="passage-text">{@html passage.html}</div>
                      </div>
                    {/each}
                  {/if}
                </div>
              {/if}

              <div
                class={part.audio || part.passages.length
                  ? "col-lg-6"
                  : "col-12"}
              >
                <h6 class="column-title mb-0">{$_("ANSWER_KEY")}</h6>
                <p class="text-muted small">{$_("ANSWER_KEY_DESCRIPTION")}</p>

                <table class="answer-table">
                  <thead>
                    <tr>
                      <th class="num-col">#</th>
                      <th>{$_("QUESTIONS")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {#each sortedQuestions(part.questions) as q (q.number)}
                      <tr
                        class="ielts-avoid-break"
                        class:row-incorrect={!q.isCorrect}
                        on:click={() => toggleRow(q)}
                        on:keydown={(e) =>
                          (e.key === "Enter" || e.key === " ") && toggleRow(q)}
                        role="button"
                        tabindex="0"
                      >
                        <td class="num-col">{q.number}</td>
                        <td>
                          <div class="question-name">
                            {@html sanitizeAssessmentHtml(q.text)}
                          </div>

                          <div
                            class="question-detail ielts-collapsible-body"
                            class:collapsed={!isRowOpen(
                              q,
                              expandedCorrectRows,
                              collapsedIncorrectRows,
                            )}
                          >
                            {#if q.options.length}
                              <div class="options-grid">
                                {#each q.options as option (option.key)}
                                  <div
                                    class:option-correct={option.key ===
                                      q.correctKey}
                                    class:option-wrong={option.key ===
                                      q.selectedKey && !q.isCorrect}
                                  >
                                    {@html sanitizeAssessmentHtml(
                                      optionLabel(option),
                                    )}
                                  </div>
                                {/each}
                              </div>
                            {/if}

                            <div class="selected-answer">
                              <span class="text-muted"
                                >{$_("YOUR_ANSWER")}:
                              </span>
                              {#if q.answered}
                                <span
                                  class={q.isCorrect
                                    ? "answer-correct"
                                    : "answer-wrong"}>{q.selectedDisplay}</span
                                >
                              {:else}
                                <span class="text-muted"
                                  >{$_("NOT_ANSWERED")}</span
                                >
                              {/if}
                            </div>

                            {#if !q.isCorrect && (correctAnswerLabel(q) || q.explanation)}
                              <div class="explanation-box">
                                {#if correctAnswerLabel(q)}
                                  <div class="mb-1">
                                    <span class="explanation-label"
                                      >{$_("CORRECT_ANSWER")}:
                                    </span>
                                    <span class="answer-correct"
                                      >{correctAnswerLabel(q)}</span
                                    >
                                  </div>
                                {/if}
                                {#if q.explanation}
                                  <div class="explanation-label">
                                    {$_("EXPLANATION")}
                                  </div>
                                  <div>{q.explanation}</div>
                                {/if}
                              </div>
                            {:else if q.isCorrect && q.explanation}
                              <div class="explanation-box">
                                <div class="explanation-label">
                                  {$_("EXPLANATION")}
                                </div>
                                <div>{q.explanation}</div>
                              </div>
                            {/if}
                          </div>
                        </td>
                      </tr>
                    {/each}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      {/each}

      <div class="total-row">
        <span>{$_("TOTAL")}</span>
        <div>
          <span class="mr-4">{report.totalQuestions} {$_("QUESTIONS")}</span>
          <span class="mr-4"
            >{report.totalCorrect} {$_("CORRECT_ANSWERS")}</span
          >
          <span
            >{report.percentage === null ? "-" : report.percentage + "%"}</span
          >
        </div>
      </div>
    {/if}

    <div class="row justify-content-center mt-4 report-footer-link">
      <a
        href="/"
        on:click={() => {
          $currentPage = "";
        }}>assessment.oksensay.com</a
      >
    </div>
  {/if}
</div>

<style>
  @media (max-width: 768px) {
    .container {
      padding: 0;
    }
  }

  .summary-box {
    padding: 1rem;
  }

  .summary-value {
    font-size: 2rem;
    font-weight: 700;
    color: #1f2937;
  }

  .summary-label {
    font-size: 0.8rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #9ca3af;
  }

  hr.hr-text {
    position: relative;
    border: none;
    height: 1px;
    background: #999;
  }

  hr.hr-text::before {
    content: attr(data-content);
    display: inline-block;
    background: #fff;
    font-weight: bold;
    font-size: 0.85rem;
    color: #999;
    border-radius: 30rem;
    padding: 0.2rem 2rem;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  .section-title {
    font-weight: 700;
    color: #1f2937;
  }

  .column-title {
    font-weight: 700;
    color: #374151;
  }

  .audio-box {
    display: flex;
    align-items: center;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    padding: 0.5rem 1rem;
  }

  .audio-box audio {
    flex: 1;
    max-width: 30rem;
    height: 2.25rem;
  }

  .passage-box {
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    padding: 1.25rem;
    margin-bottom: 1rem;
  }

  .passage-title {
    font-weight: 700;
    margin-bottom: 0.5rem;
    color: #374151;
  }

  /* No max-height/overflow here on purpose: the passage always prints and
     reads in full rather than inside its own scroll area. */
  .passage-text {
    font-size: 0.875rem;
    color: #4b5563;
    line-height: 1.6;
  }

  .passage-text :global(mark.evidence-mark) {
    background: #fad4d4;
    border-radius: 0.25rem;
    padding: 0.1rem 0.25rem;
  }

  .passage-text :global(strong.evidence-number) {
    color: #991b1b;
    font-size: 0.75rem;
    margin-right: 0.25rem;
  }

  .passage-text :global(strong.gap-marker) {
    color: #0056b3;
    font-size: 0.8rem;
  }

  .answer-table {
    width: 100%;
    border-collapse: collapse;
    border-top: 1px solid #e5e7eb;
    table-layout: fixed;
  }

  .answer-table th {
    padding: 0.75rem 0.5rem;
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    color: #374151;
    border-bottom: 1px solid #e5e7eb;
  }

  .answer-table td {
    padding: 0.75rem 0.5rem;
    font-size: 0.875rem;
    color: #1f2937;
    vertical-align: top;
  }

  .num-col {
    width: 3rem;
    text-align: center;
  }

  .answer-table tbody tr {
    border-top: 1px solid #e5e7eb;
    cursor: pointer;
  }

  .answer-table tbody tr:hover {
    background: #f3f4f6;
  }

  .answer-table tbody tr.row-incorrect {
    background: #fce8e8;
    border-top: none;
    border-bottom: 8px solid #fff;
  }

  .answer-table tbody tr.row-incorrect:hover {
    background: #fad4d4;
  }

  .question-name {
    font-weight: 500;
  }

  .question-detail {
    margin-top: 0.75rem;
  }

  .options-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 0.35rem;
    font-size: 0.8rem;
    color: #4b5563;
    margin-bottom: 0.75rem;
  }

  @media (min-width: 576px) {
    .options-grid {
      grid-template-columns: 1fr 1fr;
    }
  }

  .option-correct {
    font-weight: 700;
    color: #15803d;
  }

  .option-wrong {
    font-weight: 700;
    color: #b91c1c;
  }

  .selected-answer {
    font-size: 0.8rem;
    margin-bottom: 0.75rem;
  }

  .answer-correct {
    font-weight: 700;
    color: #15803d;
  }

  .answer-wrong {
    font-weight: 700;
    color: #b91c1c;
  }

  .explanation-box {
    font-size: 0.8rem;
    line-height: 1.6;
    color: #14532d;
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    border-radius: 0.375rem;
    padding: 0.75rem;
  }

  .explanation-label {
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-size: 0.65rem;
    color: #166534;
  }

  .total-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #f2f2f2;
    font-weight: 700;
    font-size: 0.875rem;
    text-transform: uppercase;
    color: #1f2937;
    padding: 1rem;
    border-radius: 0.375rem;
    margin-top: 1rem;
  }

  /* Wider than Bootstrap's .container cap, with tighter side padding, so the
     report uses the width a full-HD screen actually has. */
  .paper-shadow {
    position: relative;
    background: #fff;
    max-width: 1500px;
    padding: 3rem 2rem;
    margin: 50px auto;
    box-shadow: 0px 2px 38px rgba(0, 0, 0, 0.2);
  }
  .paper-shadow:after,
  .paper-shadow:before {
    content: "";
    position: absolute;
    left: auto;
    background: none;
    z-index: -1;
  }
  .paper-shadow:after {
    width: 90%;
    height: 10px;
    top: 30px;
    right: 8px;
    transform: rotate(-3deg);
    box-shadow: 0px -25px 35px 0px rgba(0, 0, 0, 0.5);
  }

  .paper-shadow:before {
    width: 10px;
    height: 95%;
    top: 5px;
    right: 18px;
    transform: rotate(3deg);
    box-shadow: 22px 0px 35px 0px rgba(0, 0, 0, 0.5);
  }
</style>
